import { Button, Text } from "@chakra-ui/react";
import { useState } from "react";
import { FormattedMessage } from "react-intl";
import { connect } from "react-redux";
import { useSelector } from "react-redux";

import { PlusIcon } from "@nens/ranalib";
import * as api from "@nens/threedi/openapi-client";

import { getV3Api } from "../apiConfiguration";
import { AppDispatch } from "../App";
import Duration from "../components/Duration";
import ErrorMessage from "../components/ErrorMessage";
import { designedRainsData } from "../data/designedRainsData";
import { remindUserToPressPlay } from "../state/mainGui";
import { getSimulation, getSimulationStatus } from "../state/simulation";
import inputElementStyles from "../styles/InputElements.module.css";
import { buttonRibbonStyle } from "../styles/styleComposition";

import { FormLabel } from "./FormLabel";
import styles from "./RainMenu.module.css";

const RainMenuDesign: React.FC<ReturnType<typeof mapDispatchToProps>> = ({
  remindUserToPressPlay,
}) => {
  const defaultSelectedDesignRain = "3";

  // Editable field
  const [chosenDesignedRainKey, setChosenDesignedRainKey] =
    useState<string>(defaultSelectedDesignRain);
  const chosenDesignedRain = designedRainsData[chosenDesignedRainKey];
  const chosenDesignRainDescription = chosenDesignedRain.description;

  const [submitting, setSubmitting] = useState<boolean>(false);
  const [communicationError, setCommunicationError] = useState<boolean>(false);

  // Current application state
  const simulation = useSelector(getSimulation);
  const simulationStatus = useSelector(getSimulationStatus);

  if (!simulation) return null; // Shouldn't happen

  // TODO: we eventually need to handle the error that design rain is longer then remaining simulation, but at the moment users cannot pause time-limited simulations and also not add rain to unpaused simulations.
  //  If one of these becomes supported we will need to implement the setCustomValidity function for a <select/> html element similar to in NumberInput react component
  //   const duration = chosenDesignedRain.duration;
  //   if (duration + getOffset() > simulation.duration) {
  //   errorMessage = <FormattedMessage id="rainmenu.error.designdurationtoolong" defaultMessage="This choice lasts longer than the remaining simulation." />

  const getOffset = (): number => {
    // We let the rain start at the current simulation time
    return simulationStatus.time || 0;
  };

  const addDesignedRainEventToSimulation = () => {
    if (!chosenDesignedRain) return;
    if (simulation === null) return;
    const rainDesignSimulationId = simulation.id;
    if (rainDesignSimulationId === undefined) return;

    const v3Api = getV3Api();
    const simulationsEventsRainTimeseriesCreateRequest = {
      simulationPk: rainDesignSimulationId,
      data: {
        offset: getOffset(),
        values: chosenDesignedRain.values,
        units: api.TimeseriesRainMsUnitEnum.M_S, // m/s is only option for now
      },
    };

    setSubmitting(true);

    v3Api
      .simulationsEventsRainTimeseriesCreate(simulationsEventsRainTimeseriesCreateRequest)
      .then(() => {
        // Reset form
        setSubmitting(false);
      })
      .catch((error) => {
        setCommunicationError(true);
        setSubmitting(false);
        console.error(
          "sending rain request failed with request",
          simulationsEventsRainTimeseriesCreateRequest,
          "error:",
          error
        );
      });

    remindUserToPressPlay();
  };

  return (
    <div className={styles.DesignRain}>
      <FormLabel>
        <FormattedMessage id="newRainMenu.design_type" defaultMessage="Design type" />
      </FormLabel>
      <select
        className={`${inputElementStyles.Padding} ${inputElementStyles.Input}`}
        onChange={(e) => setChosenDesignedRainKey(e.target.value)}
        value={chosenDesignedRainKey}
      >
        {Object.keys(designedRainsData).map((eventType) => (
          <option key={eventType} value={eventType}>
            {eventType +
              (designedRainsData[eventType].onceInAmountOfYears
                ? ` (t= ${designedRainsData[eventType].onceInAmountOfYears} years)`
                : "")}
          </option>
        ))}
      </select>
      <FormLabel>
        <FormattedMessage id="newRainMenu.total_rainfall_label" defaultMessage="Total rainfall" />
      </FormLabel>
      <Text>
        {chosenDesignedRain.total}{" "}
        <FormattedMessage id="newRainMenu.total_rainfall_unit_mm" defaultMessage="mm" />
      </Text>
      <FormLabel>
        <FormattedMessage id="newRainMenu.duration_label" defaultMessage="Duration" />
      </FormLabel>
      <Text>
        <Duration duration={chosenDesignedRain.duration} />
      </Text>
      {chosenDesignRainDescription !== "" ? (
        <Text mt="8px" fontSize="12px" color="brand.clear_creek" fontStyle="italic">
          {chosenDesignRainDescription}
        </Text>
      ) : null}

      <div className={buttonRibbonStyle}>
        <Button
          variant="secondary"
          leftIcon={<PlusIcon />}
          onClick={addDesignedRainEventToSimulation}
          isDisabled={submitting}
        >
          <FormattedMessage id="newRainMenu.create_rain_button" defaultMessage="Create" />
        </Button>
      </div>
      <ErrorMessage
        error={communicationError}
        errorMessage={
          <FormattedMessage id="communicationerror" defaultMessage="Failed to send request." />
        }
      />
    </div>
  );
};

const mapDispatchToProps = (dispatch: AppDispatch) => {
  return {
    remindUserToPressPlay: () => dispatch(remindUserToPressPlay()),
  };
};

export default connect(null, mapDispatchToProps)(RainMenuDesign);
